import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded';
import AddShoppingCartRoundedIcon from '@mui/icons-material/AddShoppingCartRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCollection, getSiteLabel, requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';
import { useLocation } from 'react-router-dom';
import { useSiteSession } from '../context/SiteSessionContext';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';

function formatFileSize(value) {
  return `${Math.round((value || 0) / 1024)} Ko`;
}

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString('fr-FR');
}

function formatSignedPower(value) {
  const number = Number(value || 0);
  const formatted = Math.abs(number).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${number < 0 ? '-' : '+'}${formatted}`;
}

function buildAmoiCustomerReference(customerName) {
  const compactName = String(customerName || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '')
    .toUpperCase();
  return compactName ? `AMOI${compactName}` : '—';
}

export default function StockOrdersPage() {
  const menuPage = useMenuPage('/stock/commandes');
  const location = useLocation();
  const isReorderPage = location.pathname === '/stock/reassort';
  const { isAdmin } = useSiteSession();
  const [blockedSuppliers, setBlockedSuppliers] = useState([]);
  const [blockSaving, setBlockSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [orderedRows, setOrderedRows] = useState([]);
  const [stockDashboard, setStockDashboard] = useState({ newOrders: 0, reorders: 0, followUps: 0 });
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState('');
  const [supplyingId, setSupplyingId] = useState('');
  const [previewLoadingId, setPreviewLoadingId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewMovementIds, setPreviewMovementIds] = useState([]);
  const [previewSearch, setPreviewSearch] = useState('');
  const [supplyConfirmRow, setSupplyConfirmRow] = useState(null);
  const [supplyQuantity, setSupplyQuantity] = useState(1);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanBarcode, setScanBarcode] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [reorderSearch, setReorderSearch] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectionModel, setSelectionModel] = useState({ type: 'include', ids: new Set() });
  const [deleteConfirmRows, setDeleteConfirmRows] = useState(null);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [payload, orderedPayload, reservationsPayload, blockedPayload] = await Promise.all([
        requestJson('/stock-orders/low-stock'),
        requestJson('/stock-orders/ordered'),
        requestJson('/stock-reservations'),
        requestJson('/stock-orders/blocked-suppliers').catch(() => null),
      ]);
      if (blockedPayload) setBlockedSuppliers(getCollection(blockedPayload));
      const reorderRows = getCollection(payload);
      const nextOrderedRows = getCollection(orderedPayload);
      setRows(reorderRows);
      setOrderedRows(nextOrderedRows);
      setStockDashboard({
        newOrders: getCollection(reservationsPayload).length,
        reorders: reorderRows.length,
        followUps: nextOrderedRows.filter(
          (row) => Number(row.remainingQty ?? row.suggestedQty ?? row.consumedQty ?? 0) > 0
        ).length,
      });
    } catch (err) {
      setError(err.message);
      setRows([]);
      setOrderedRows([]);
      setStockDashboard({ newOrders: 0, reorders: 0, followUps: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const supplierOptions = useMemo(
    () =>
      Array.from(new Set([...rows, ...orderedRows].map((row) => String(row.supplier || '').trim()).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, 'fr')
      ),
    [orderedRows, rows]
  );

  const selectedSupplier = supplierFilter || '';
  const isSupplierBlocked = useCallback(
    (supplier) => blockedSuppliers.includes(String(supplier || '').trim().toUpperCase()),
    [blockedSuppliers]
  );
  const selectedSupplierBlocked = isSupplierBlocked(selectedSupplier);

  const handleToggleSupplierBlock = async () => {
    if (!selectedSupplier) return;
    try {
      setBlockSaving(true);
      setError('');
      setMessage('');
      const payload = await requestJson('/stock-orders/blocked-suppliers', {
        method: 'PUT',
        body: JSON.stringify({ supplier: selectedSupplier, blocked: !selectedSupplierBlocked }),
      });
      setBlockedSuppliers(getCollection(payload));
      setMessage(selectedSupplierBlocked
        ? `Envoi des commandes débloqué pour ${selectedSupplier}.`
        : `Envoi des commandes bloqué pour ${selectedSupplier}.`);
    } catch (err) {
      setError(err.message);
    } finally {
      setBlockSaving(false);
    }
  };

  const filteredRows = useMemo(
    () => rows.filter((row) => !selectedSupplier || String(row.supplier || '').trim() === selectedSupplier),
    [rows, selectedSupplier]
  );

  const filteredOrderedRows = useMemo(
    () => orderedRows.filter((row) => !selectedSupplier || String(row.supplier || '').trim() === selectedSupplier),
    [orderedRows, selectedSupplier]
  );

  const handleOpenPreview = useCallback(async ({ supplier = selectedSupplier, movementIds = [] } = {}) => {
    if (!supplier) {
      setError('Sélectionne un fournisseur avant de préparer l’envoi.');
      return;
    }
    try {
      setPreviewLoadingId(movementIds[0] || supplier);
      setError('');
      setMessage('');
      const query = new URLSearchParams({ supplier });
      if (movementIds.length) query.set('movementIds', movementIds.join(','));
      const payload = await requestJson(`/stock-orders/supplier-email-preview?${query.toString()}`);
      setPreview(payload);
      setPreviewMovementIds(movementIds);
      setPreviewSearch('');
    } catch (err) {
      setError(err.message);
    } finally {
      setPreviewLoadingId('');
    }
  }, [selectedSupplier]);

  const handleSend = async () => {
    const supplierToSend = preview?.supplier || selectedSupplier;
    if (!preview?.items?.length || !supplierToSend) return;
    try {
      setSendingId(supplierToSend);
      setError('');
      setMessage('');
      const result = await requestJson('/stock-orders/send-supplier-email', {
        method: 'POST',
        body: JSON.stringify({ supplier: supplierToSend, movementIds: previewMovementIds }),
      });
      setMessage(
        result.mail?.skipped
          ? `${result.count || preview.items.length} référence(s) préparée(s) pour ${result.mail.to}, mais SMTP atelier inactif.`
          : `${result.count || preview.items.length} référence(s) envoyée(s) à ${result.mail?.to || supplierToSend}.`
      );
      setPreview(null);
      setPreviewMovementIds([]);
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSendingId('');
    }
  };

  const handleReopen = useCallback(async (row) => {
    try {
      setError('');
      setMessage('');
      await requestJson(`/stock-orders/${encodeURIComponent(row._id)}/reopen`, {
        method: 'PATCH',
        body: JSON.stringify({ movementIds: row.movementIds || [] }),
      });
      setMessage(`La référence ${row.barcode} est remise dans les lignes à commander.`);
      await loadRows();
    } catch (err) {
      setError(err.message);
    }
  }, [loadRows]);

  const handleSupply = useCallback(async (row) => {
    const requestedQuantity = Number(supplyQuantity || 0);
    const remainingQuantity = Number(row.remainingQty || row.suggestedQty || row.consumedQty || 1);
    if (!Number.isFinite(requestedQuantity) || requestedQuantity <= 0) {
      setError('Saisis une quantité à approvisionner valide.');
      return;
    }
    if (requestedQuantity > remainingQuantity) {
      setError(`Quantité trop élevée. Restant à approvisionner : ${remainingQuantity}.`);
      return;
    }
    try {
      setSupplyingId(row._id);
      setError('');
      setMessage('');
      await requestJson(`/stock-orders/${encodeURIComponent(row._id)}/supply`, {
        method: 'POST',
        body: JSON.stringify({ quantity: requestedQuantity }),
      });
      const nextRemaining = Math.max(remainingQuantity - requestedQuantity, 0);
      setMessage(
        nextRemaining
          ? `Le stock du code barre ${row.barcode} a été approvisionné de ${requestedQuantity}. Restant à recevoir : ${nextRemaining}.`
          : `Le stock du code barre ${row.barcode} a été totalement approvisionné.`
      );
      setSupplyConfirmRow(null);
      setSupplyQuantity(1);
      setScanDialogOpen(false);
      setScanBarcode('');
      await loadRows();
    } catch (err) {
      setError(err.message);
    } finally {
      setSupplyingId('');
    }
  }, [loadRows, supplyQuantity]);

  const openSupplyConfirm = useCallback((row) => {
    setError('');
    setMessage('');
    setSupplyQuantity(Number(row.remainingQty || row.suggestedQty || row.consumedQty || 1));
    setSupplyConfirmRow(row);
  }, []);

  const gridRows = useMemo(
    () =>
      filteredRows.map((row) => ({
        ...row,
        id: row._id,
        workshopLabel: [row.siteId?.code, getSiteLabel(row.siteId)].filter(Boolean).join(' - '),
        supplierAccount: row.supplierAccount || row.replenishmentOrderEmail?.account || '',
        consumedQty: Number(row.consumedQty || 0),
        suggestedQty: Number(row.suggestedQty || 1),
        jobsLabel: (row.jobs || []).join(', '),
        customerLabel: row.customerName || (row.customers || []).join(', ') || '—',
        reference: row.customerReference || buildAmoiCustomerReference(row.customerName || (row.customers || []).join(', ')),
      })),
    [filteredRows]
  );

  const orderedGridRows = useMemo(
    () =>
      filteredOrderedRows.map((row) => ({
        ...row,
        id: row._id,
        workshopLabel: [row.siteId?.code, getSiteLabel(row.siteId)].filter(Boolean).join(' - '),
        supplierAccount: row.supplierAccount || row.replenishmentOrderEmail?.account || '',
        consumedQty: Number(row.consumedQty || row.replenishmentOrderEmail?.consumedQty || 0),
        suggestedQty: Number(row.suggestedQty || row.remainingQty || row.replenishmentOrderEmail?.suggestedQty || 1),
        orderedQty: Number(row.orderedQty || row.replenishmentOrderEmail?.quantity || row.suggestedQty || 1),
        remainingQty: Number(row.remainingQty ?? row.suggestedQty ?? 1),
        jobsLabel: (row.jobs || []).join(', '),
        customerLabel: row.customerName || (row.customers || []).join(', ') || '—',
        reference: row.customerReference || buildAmoiCustomerReference(row.customerName || (row.customers || []).join(', ')),
        orderedAtLabel: formatDateTime(row.orderedAt || row.replenishmentOrderEmail?.sentAt),
        orderedTo: row.orderedTo || row.replenishmentOrderEmail?.to || '—',
        replenishedAtLabel: formatDateTime(row.replenishedAt || row.replenishmentReceived?.receivedAt),
        replenishedQty: Number(row.replenishedQty || row.replenishmentReceived?.quantity || 0),
        isReplenished: Number(row.remainingQty ?? row.suggestedQty ?? 0) <= 0,
      })),
    [filteredOrderedRows]
  );

  const filteredOrderedGridRows = useMemo(() => {
    const search = reorderSearch.trim().toLocaleLowerCase('fr-FR');
    if (!search) return orderedGridRows;
    return orderedGridRows.filter((row) => [
      row.supplier,
      row.supplierAccount,
      row.barcode,
      row.label,
      row.workshopLabel,
      row.jobsLabel,
      row.customerLabel,
      row.reference,
      row.orderedTo,
    ].some((value) => String(value || '').toLocaleLowerCase('fr-FR').includes(search)));
  }, [orderedGridRows, reorderSearch]);

  const selectedMovementIds = useMemo(() => {
    const ids = new Set(Array.from(selectionModel?.ids || []).map(String));
    const selectedRows = gridRows.filter((row) => selectionModel?.type === 'exclude' ? !ids.has(String(row.id)) : ids.has(String(row.id)));
    return selectedRows.flatMap((row) => row.movementIds || []);
  }, [gridRows, selectionModel]);

  const selectedPreviewMovementIds = selectedMovementIds.length
    ? selectedMovementIds
    : gridRows.flatMap((row) => row.movementIds || []);
  const selectedRowIds = new Set(Array.from(selectionModel?.ids || []).map(String));
  const selectedPreviewRows = gridRows.filter((row) => selectionModel?.type === 'exclude' ? !selectedRowIds.has(String(row.id)) : selectedRowIds.has(String(row.id)));
  const selectedPreviewSuppliers = [...new Set(selectedPreviewRows.map((row) => row.supplier).filter(Boolean))];
  const previewSupplier = selectedSupplier || (selectedPreviewSuppliers.length === 1 ? selectedPreviewSuppliers[0] : '');

  const handleDeleteSelected = () => {
    if (!selectedMovementIds.length) return;
    const ids = new Set(Array.from(selectionModel?.ids || []).map(String));
    setDeleteConfirmRows(gridRows.filter((row) => selectionModel?.type === 'exclude' ? !ids.has(String(row.id)) : ids.has(String(row.id))));
  };

  const confirmDeleteSelected = async () => {
    if (!selectedMovementIds.length) return;
    try {
      await requestJson('/stock-orders/reorder-lines', { method: 'DELETE', body: JSON.stringify({ movementIds: selectedMovementIds }) });
      setSelectionModel({ type: 'include', ids: new Set() });
      setMessage(`${selectedMovementIds.length} sortie(s) supprimée(s) des recommandations.`);
      setDeleteConfirmRows(null);
      await loadRows();
    } catch (err) { setError(err.message); }
  };

  const scannedOrderedRows = useMemo(() => {
    const barcode = scanBarcode.trim().toLowerCase();
    if (!barcode) return [];
    return orderedGridRows.filter(
      (row) => !row.isReplenished && String(row.barcode || '').trim().toLowerCase() === barcode
    );
  }, [orderedGridRows, scanBarcode]);

  const filteredPreviewRows = useMemo(() => {
    const search = previewSearch.trim().toLowerCase();
    if (!search) return preview?.mail?.rows || [];
    return (preview?.mail?.rows || []).filter((row) => [
      row.reference, row.customerName, row.storeCode, row.account, row.barcode, row.label,
    ].some((value) => String(value || '').toLowerCase().includes(search)));
  }, [preview, previewSearch]);

  const columns = useMemo(
    () => [
      { field: 'supplier', headerName: 'Fournisseur', minWidth: 140, flex: 0.65 },
      { field: 'supplierAccount', headerName: 'Compte', minWidth: 120, flex: 0.5 },
      { field: 'barcode', headerName: 'Code barre', minWidth: 140, flex: 0.7 },
      { field: 'label', headerName: 'Libellé', minWidth: 280, flex: 1.5 },
      { field: 'workshopLabel', headerName: 'Atelier', minWidth: 170, flex: 0.85 },
      { field: 'jobsLabel', headerName: 'Jobs', minWidth: 180, flex: 0.9 },
      { field: 'customerLabel', headerName: 'Nom / prénom client', minWidth: 220, flex: 1 },
      { field: 'reference', headerName: 'Référence', minWidth: 220, flex: 1 },
      {
        field: 'sphere',
        headerName: 'Sphère',
        minWidth: 100,
        flex: 0.45,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value) => formatSignedPower(value),
      },
      {
        field: 'cylinder',
        headerName: 'Cylindre',
        minWidth: 100,
        flex: 0.45,
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (value) => formatSignedPower(value),
      },
      {
        field: 'suggestedQty',
        headerName: 'Quantité',
        minWidth: 120,
        flex: 0.45,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => <Chip size="small" color="primary" label={params.value} sx={{ fontWeight: 900 }} />,
      },
      {
        field: 'actions',
        headerName: 'Action',
        minWidth: 135,
        sortable: false,
        filterable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Tooltip title={isSupplierBlocked(params.row.supplier) ? `Envoi bloqué pour ${params.row.supplier}` : 'Prévisualiser et envoyer uniquement cette référence'}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SendRoundedIcon />}
                onClick={() => handleOpenPreview({ supplier: params.row.supplier, movementIds: params.row.movementIds || [] })}
                disabled={Boolean(previewLoadingId) || isSupplierBlocked(params.row.supplier)}
              >
                Envoyer
              </Button>
            </span>
          </Tooltip>
        ),
      },
    ],
    [handleOpenPreview, isSupplierBlocked, previewLoadingId]
  );

  const orderedColumns = useMemo(
    () => {
      const columns = [
      {
        field: 'actions',
        headerName: 'Action',
        width: 300,
        sortable: false,
        filterable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" sx={{ width: '100%' }}>
            {params.row.isReplenished ? (
              <Chip
                size="small"
                color="success"
                label={`Approvisionné${params.row.replenishedQty ? ` +${params.row.replenishedQty}` : ''}`}
                sx={{ fontWeight: 850 }}
              />
            ) : (
              <Tooltip title="Ajouter cette quantité au stock du bon code barre">
                <span>
                  <Button
                    size="small"
                    startIcon={<AddBoxRoundedIcon />}
                    onClick={() => openSupplyConfirm(params.row)}
                    disabled={supplyingId === params.row._id}
                  >
                    Approvisionner
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Remettre dans les lignes à commander">
              <Button size="small" startIcon={<ReplayRoundedIcon />} onClick={() => handleReopen(params.row)}>
                Relancer
              </Button>
            </Tooltip>
          </Stack>
        ),
      },
      { field: 'jobsLabel', headerName: 'JOB', minWidth: 180, flex: 0.9 },
      { field: 'reference', headerName: 'Référence', minWidth: 220, flex: 1 },
      { field: 'supplierAccount', headerName: 'Compte', minWidth: 120, flex: 0.5 },
      { field: 'barcode', headerName: 'Code barre', minWidth: 140, flex: 0.7 },
      { field: 'label', headerName: 'Libellé', minWidth: 260, flex: 1.35 },
      {
        field: 'suggestedQty',
        headerName: 'Restant',
        minWidth: 120,
        flex: 0.45,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => <Chip size="small" color="primary" label={params.value} sx={{ fontWeight: 900 }} />,
      },
      {
        field: 'replenishedQty',
        headerName: 'Déjà reçu',
        minWidth: 120,
        flex: 0.45,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => <Chip size="small" color={params.value ? 'success' : 'default'} label={params.value || 0} sx={{ fontWeight: 850 }} />,
      },
      { field: 'orderedAtLabel', headerName: 'Envoyé le', minWidth: 170, flex: 0.8 },
      { field: 'orderedTo', headerName: 'Destinataire', minWidth: 230, flex: 1 },
      ];
      return columns;
    },
    [handleReopen, openSupplyConfirm, supplyingId]
  );

  const scanColumns = useMemo(
    () => [
      { field: 'supplier', headerName: 'Fournisseur', minWidth: 130, flex: 0.75 },
      { field: 'barcode', headerName: 'Code barre', minWidth: 140, flex: 0.75 },
      { field: 'label', headerName: 'Libellé', minWidth: 260, flex: 1.4 },
      { field: 'storeLabel', headerName: 'Magasin', minWidth: 150, flex: 0.8 },
      { field: 'jobsLabel', headerName: 'Jobs', minWidth: 160, flex: 0.8 },
      {
        field: 'suggestedQty',
        headerName: 'Restant',
        minWidth: 90,
        flex: 0.35,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => <Chip size="small" color="primary" label={params.value} sx={{ fontWeight: 900 }} />,
      },
      {
        field: 'scanAction',
        headerName: 'Action',
        width: 150,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Button size="small" variant="contained" onClick={() => openSupplyConfirm(params.row)}>
            Choisir
          </Button>
        ),
      },
    ],
    [openSupplyConfirm]
  );

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>
            {menuPage.sectionTitle}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>
            Commandes - Verre de stock
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" startIcon={<QrCodeScannerRoundedIcon />} onClick={() => setScanDialogOpen(true)}>
            Approvisionner
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadRows}>
            Rafraîchir
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
        {[
          {
            label: 'Nouvelles commandes',
            value: stockDashboard.newOrders,
            detail: 'Réservations en attente de validation',
            icon: <AddShoppingCartRoundedIcon />,
            color: '#2563eb',
            background: 'rgba(37, 99, 235, 0.13)',
          },
          {
            label: 'Recommandes de verres',
            value: stockDashboard.reorders,
            detail: 'Sorties à préparer chez le fournisseur',
            icon: <AutorenewRoundedIcon />,
            color: '#0f9b8e',
            background: 'rgba(13, 148, 136, 0.14)',
          },
          {
            label: 'Relances à faire',
            value: stockDashboard.followUps,
            detail: 'Commandes fournisseur non réceptionnées',
            icon: <NotificationsActiveRoundedIcon />,
            color: '#f59e0b',
            background: 'rgba(245, 158, 11, 0.15)',
          },
        ].map((card) => (
          <Card key={card.label} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 900, lineHeight: 1.1 }}>{card.label}</Typography>
                  <Typography variant="h3" sx={{ mt: 0.5, fontWeight: 950, lineHeight: 1 }}>{card.value}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{card.detail}</Typography>
                </Box>
                <Box sx={{ width: 48, height: 48, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: card.background, color: card.color, flexShrink: 0 }}>
                  {card.icon}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>

      {!isReorderPage && <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <Inventory2RoundedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Sorties à recommander
              </Typography>
              <Typography color="text.secondary">
                {rows.length} sortie{rows.length > 1 ? 's' : ''} de commande à recommander, {orderedRows.length} déjà commandée{orderedRows.length > 1 ? 's' : ''}.
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Fournisseur"
              value={supplierFilter}
              onChange={(event) => setSupplierFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 260 } }}
            >
              <MenuItem value="">Tous les fournisseurs</MenuItem>
              {supplierOptions.map((supplier) => (
                <MenuItem key={supplier} value={supplier}>
                  {supplier}{isSupplierBlocked(supplier) ? ' — envoi bloqué' : ''}
                </MenuItem>
              ))}
            </TextField>
            <Chip
              label={`${gridRows.length} référence${gridRows.length > 1 ? 's' : ''} affichée${gridRows.length > 1 ? 's' : ''}`}
              color={selectedSupplier ? 'primary' : 'default'}
              variant={selectedSupplier ? 'filled' : 'outlined'}
              sx={{ fontWeight: 850 }}
            />
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<SendRoundedIcon />}
              onClick={() => handleOpenPreview({ supplier: previewSupplier, movementIds: selectedPreviewMovementIds })}
              disabled={!previewSupplier || !gridRows.length || Boolean(previewLoadingId) || isSupplierBlocked(previewSupplier)}
              sx={{ fontWeight: 900 }}
            >
              {previewLoadingId ? 'Préparation...' : 'Prévisualiser l’envoi fournisseur'}
            </Button>
            <Button color="error" variant="outlined" startIcon={<DeleteRoundedIcon />} onClick={handleDeleteSelected} disabled={!selectedMovementIds.length}>
              Supprimer les sorties sélectionnées
            </Button>
            {isAdmin && selectedSupplier && (
              <Button
                color={selectedSupplierBlocked ? 'success' : 'warning'}
                variant="outlined"
                startIcon={selectedSupplierBlocked ? <LockOpenRoundedIcon /> : <BlockRoundedIcon />}
                onClick={handleToggleSupplierBlock}
                disabled={blockSaving}
              >
                {selectedSupplierBlocked ? `Débloquer l’envoi ${selectedSupplier}` : `Bloquer l’envoi ${selectedSupplier}`}
              </Button>
            )}
          </Stack>

          {selectedSupplierBlocked && (
            <Alert severity="warning" icon={<BlockRoundedIcon />} sx={{ mb: 2 }}>
              L’envoi des commandes de verres de stock est bloqué pour {selectedSupplier}.
              {isAdmin ? ' Utilise « Débloquer » pour le réautoriser.' : ' Contacte la Direction pour le débloquer.'}
            </Alert>
          )}

          {!selectedSupplier && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Filtre par fournisseur pour préparer un mail groupé avec l’Excel des sorties de verres de stock à recommander.
            </Alert>
          )}

          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <DataGrid
              rows={gridRows}
              columns={columns}
              checkboxSelection
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={setSelectionModel}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              autoHeight
              sx={{ border: 0 }}
            />
          )}
        </CardContent>
      </Card>}

      {isReorderPage && <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
            <ReplayRoundedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Réassort
              </Typography>
              <Typography color="text.secondary">
                Sorties de verres de stock pour lesquelles un mail fournisseur a déjà été envoyé.
              </Typography>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
            <TextField
              size="small"
              label="Rechercher dans le réassort"
              placeholder="Référence, code-barres, compte, fournisseur, JOB, client…"
              value={reorderSearch}
              onChange={(event) => setReorderSearch(event.target.value)}
              fullWidth
            />
            <Chip
              label={`${filteredOrderedGridRows.length} ligne${filteredOrderedGridRows.length > 1 ? 's' : ''}`}
              variant="outlined"
              sx={{ fontWeight: 850, alignSelf: { xs: 'flex-start', sm: 'center' } }}
            />
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <DataGrid
              rows={filteredOrderedGridRows}
              columns={orderedColumns}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              sx={{ border: 0 }}
            />
          )}
        </CardContent>
      </Card>}

      <Dialog open={Boolean(deleteConfirmRows)} onClose={() => setDeleteConfirmRows(null)} fullWidth maxWidth="md">
        <DialogTitle>Confirmer la suppression des sorties</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ces sorties seront retirées des recommandations fournisseur. L’historique des mouvements sera conservé.
          </Alert>
          <Typography sx={{ fontWeight: 900, mb: 1 }}>
            {deleteConfirmRows?.length || 0} sortie(s) sélectionnée(s)
          </Typography>
          <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow><TableCell>Fournisseur</TableCell><TableCell>Code barre</TableCell><TableCell>Libellé</TableCell><TableCell>Quantité</TableCell></TableRow></TableHead>
              <TableBody>
                {(deleteConfirmRows || []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.supplier || '—'}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{row.barcode || '—'}</TableCell>
                    <TableCell>{row.label || '—'}</TableCell>
                    <TableCell>{row.suggestedQty || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmRows(null)}>Annuler</Button>
          <Button color="error" variant="contained" startIcon={<DeleteRoundedIcon />} onClick={confirmDeleteSelected}>
            Confirmer la suppression
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(preview)}
        onClose={() => {
          if (sendingId) return;
          setPreview(null);
          setPreviewMovementIds([]);
        }}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>Prévisualisation du mail fournisseur</DialogTitle>
        <DialogContent>
          {preview && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Vérifie le contenu avant l'envoi. Le fichier Excel sera joint au mail fournisseur.
              </Alert>
              <TextField
                size="small"
                label="Rechercher dans les lignes"
                placeholder="Référence, client, magasin, code-barres, libellé…"
                value={previewSearch}
                onChange={(event) => setPreviewSearch(event.target.value)}
                fullWidth
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '140px 1fr' }, gap: 1 }}>
                <Typography color="text.secondary">À</Typography>
                <Typography sx={{ fontWeight: 850 }}>{preview.mail.to}</Typography>
                <Typography color="text.secondary">Copie</Typography>
                <Typography>{preview.mail.cc || '—'}</Typography>
                <Typography color="text.secondary">Sujet</Typography>
                <Typography sx={{ fontWeight: 850 }}>{preview.mail.subject}</Typography>
              </Box>
              <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, maxHeight: 360 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Référence</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell>Code mag</TableCell>
                      <TableCell>Compte fournisseur</TableCell>
                      <TableCell>Code barre</TableCell>
                      <TableCell>Libellé</TableCell>
                      <TableCell align="center">Sphère</TableCell>
                      <TableCell align="center">Cylindre</TableCell>
                      <TableCell align="center">Quantité</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPreviewRows.map((row, index) => (
                      <TableRow key={row._id || row.movementIds?.join('-') || `${row.workshop}-${row.barcode}-${index}`}>
                        <TableCell sx={{ fontWeight: 900 }}>
                          {row.reference || row.customerReference || buildAmoiCustomerReference(row.customerName || (row.customers || []).join(', '))}
                        </TableCell>
                        <TableCell>{row.customerName || (row.customers || []).join(', ') || '—'}</TableCell>
                        <TableCell>{row.storeCode || '—'}</TableCell>
                        <TableCell>{row.account || '—'}</TableCell>
                        <TableCell sx={{ fontWeight: 900 }}>{row.barcode}</TableCell>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="center">{formatSignedPower(row.sphere)}</TableCell>
                        <TableCell align="center">{formatSignedPower(row.cylinder)}</TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={row.suggestedQty} color="primary" />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!filteredPreviewRows.length && (
                      <TableRow><TableCell colSpan={9} align="center">Aucune ligne ne correspond à la recherche.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                  Pièces jointes
                </Typography>
                {(preview.mail.attachments || []).map((attachment) => (
                  <Stack key={attachment.id || attachment.name} direction="row" spacing={1} alignItems="center">
                    <AttachFileIcon fontSize="small" color="primary" />
                    <Chip size="small" label="Excel réassort" color="success" />
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {attachment.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(attachment.size)}
                      {attachment.rowCount ? ` · ${attachment.rowCount} ligne${attachment.rowCount > 1 ? 's' : ''}` : ''}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPreview(null);
              setPreviewMovementIds([]);
            }}
            disabled={Boolean(sendingId)}
          >
            Annuler
          </Button>
          <Button variant="contained" startIcon={<SendRoundedIcon />} onClick={handleSend} disabled={Boolean(sendingId) || Boolean(preview?.blocked) || isSupplierBlocked(preview?.supplier)}>
            {sendingId ? 'Envoi...' : 'Confirmer l’envoi'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scanDialogOpen} onClose={() => !supplyingId && setScanDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Approvisionner par scan</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Scanner le code barre du verre"
              value={scanBarcode}
              onChange={(event) => setScanBarcode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault();
              }}
              placeholder="Code barre"
              fullWidth
            />
            {scanBarcode.trim() && !scannedOrderedRows.length && (
              <Alert severity="warning">
                Aucune commande déjà envoyée et non approvisionnée trouvée pour ce code barre.
              </Alert>
            )}
            <DataGrid
              rows={scannedOrderedRows}
              columns={scanColumns}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              pageSizeOptions={[5, 10, 25]}
              disableRowSelectionOnClick
              autoHeight
              sx={{ border: 0 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanDialogOpen(false)} disabled={Boolean(supplyingId)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(supplyConfirmRow)}
        onClose={() => {
          if (supplyingId) return;
          setSupplyConfirmRow(null);
          setSupplyQuantity(1);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirmer l’approvisionnement</DialogTitle>
        <DialogContent>
          {supplyConfirmRow && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="warning">
                Cette action va ajouter la quantité saisie au stock du code barre sélectionné. Si la réception est partielle, la ligne restera avec le restant.
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 1 }}>
                <Typography color="text.secondary">Code barre</Typography>
                <Typography sx={{ fontWeight: 900 }}>{supplyConfirmRow.barcode}</Typography>
                <Typography color="text.secondary">Fournisseur</Typography>
                <Typography sx={{ fontWeight: 850 }}>{supplyConfirmRow.supplier || '—'}</Typography>
                <Typography color="text.secondary">Libellé</Typography>
                <Typography>{supplyConfirmRow.label || '—'}</Typography>
                <Typography color="text.secondary">Magasin</Typography>
                <Typography>{supplyConfirmRow.storeLabel || '—'}</Typography>
                <Typography color="text.secondary">Jobs</Typography>
                <Typography>{supplyConfirmRow.jobsLabel || '—'}</Typography>
                <Typography color="text.secondary">Déjà reçu</Typography>
                <Typography>{supplyConfirmRow.replenishedQty || 0}</Typography>
                <Typography color="text.secondary">Restant</Typography>
                <Chip
                  size="small"
                  color="primary"
                  label={supplyConfirmRow.remainingQty || supplyConfirmRow.suggestedQty || supplyConfirmRow.consumedQty || 1}
                  sx={{ width: 'fit-content', fontWeight: 900 }}
                />
              </Box>
              <TextField
                label="Quantité reçue"
                type="number"
                value={supplyQuantity}
                onChange={(event) => setSupplyQuantity(Number(event.target.value))}
                inputProps={{
                  min: 1,
                  max: supplyConfirmRow.remainingQty || supplyConfirmRow.suggestedQty || supplyConfirmRow.consumedQty || 1,
                  step: 1,
                }}
                helperText={`Maximum restant : ${supplyConfirmRow.remainingQty || supplyConfirmRow.suggestedQty || supplyConfirmRow.consumedQty || 1}`}
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSupplyConfirmRow(null);
              setSupplyQuantity(1);
            }}
            disabled={Boolean(supplyingId)}
          >
            Annuler
          </Button>
          <Button
            variant="contained"
            startIcon={<AddBoxRoundedIcon />}
            onClick={() => supplyConfirmRow && handleSupply(supplyConfirmRow)}
            disabled={Boolean(supplyingId)}
          >
            {supplyingId ? 'Approvisionnement...' : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
